import {
  getDesignDataByDesignAreaIdentifier,
  getSelectedDesignProductionMethodIdentifier,
  designAreaAppliesMask,
  getVisualizationEffectWithOptions,
} from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import { requestLock } from '@configuratorware/configurator-frontendgui/Utils/Async/ObjectLock';
import { getFontMapping } from '@configuratorware/configurator-frontendgui/App/Reducers/Fonts/Selectors';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import BaseDesignDataService, {
  isWidget,
  updateColorAmountOnDesignProductionMethodChange,
} from '@configuratorware/configurator-frontendgui/App/Services/DesignDataService';
import { createSVGImageData } from '@configuratorware/configurator-frontendgui/App/Reducers/ImageGallery/Modifiers';
import { getPreviewUrlFromImageData } from '@configuratorware/configurator-frontendgui/App/Reducers/ImageGallery/Selectors';
import {
  isDesignerProductPreviewMode,
  getClientHighlightColor,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration.js';
import { get } from 'lodash';
import placeholderLogo from '../../../../public/images/designer_placeholder_logo.png';
import setupDesigner from '../Modules/Designer/setupDesigner';

export default class DesignDataService extends BaseDesignDataService {
  async addSampleImageForDesignAreas(designAreaIdentifiers, active) {
    await Promise.all(
      designAreaIdentifiers.map(async (designAreaIdentifier) => {
        const designArea = this.getDesignAreaByIdentifier(designAreaIdentifier);
        const canvas = this._getCanvas(designArea);
        const state = Services.store.state;
        const imageSrc = placeholderLogo;
        if (active) {
          const imageData = createSVGImageData({
            fileName: 'designer_placeholder_logo.png',
            imageUrl: imageSrc,
            thumbUrl: imageSrc,
            isPlaceHolderImage: true,
            galleryImage: false,
          });
          await Services.designer.addImageFromUrl(
            canvas,
            getPreviewUrlFromImageData(imageData.preview),
            imageData,
            { enlargeImage: true }
          );
          const canvasDataJSON = this.calculateObjectPosition(canvas);
          // eslint-disable-next-line no-console
          console.log(`Sample image added to canvas: ${designAreaIdentifier}`);
          this.setCanvasData(canvasDataJSON, designAreaIdentifier);
        } else {
          const designData = getDesignDataByDesignAreaIdentifier(state, designAreaIdentifier);
          const { canvasData } = designData;
          if (!canvasData) {
            return;
          }
          const objects = canvasData ? canvasData.objects.filter((obj) => obj.type === 'Text') : [];
          this.setCanvasData({ ...canvasData, objects }, designAreaIdentifier);
          if (designArea) {
            await this.loadCanvasDataFromState([designArea]);
          }
        }
        return {
          canvas,
          designAreaIdentifier,
        };
      })
    );
  }

  async redrawAllDesignAreas(getDrawingContext, updateEventObject) {
    const options = {
      textureBuilderConfig: {
        highlight: {
          lineWidth: 6, // increasing thickness of the border line, default was 2
        },
      },
    };
    await super.redrawAllDesignAreas(getDrawingContext, updateEventObject, options);
  }

  init() {
    Services.store.observeStore(
      (state) => state.designArea.filteredDesignAreas,
      async (filteredDesignAreas, prevDesignAreas) => {
        try {
          const initList = await this.initCanvases(filteredDesignAreas, prevDesignAreas);
          // only the newly initialized canvases has to be loaded
          const areasToLoad = initList.filter(({ isNew }) => isNew).map(({ designArea }) => designArea);
          // the canvas data is there first with the configuration, but since the canvas instances are
          // associated with the design areas the data can be loaded only when the design areas are available
          !isDesignerProductPreviewMode() &&
            areasToLoad.length &&
            (await this.loadCanvasDataFromState(areasToLoad));

          this.selectInitialDesignView();
          this.designAreaListChangeListener(filteredDesignAreas, prevDesignAreas);
          this.openDesignAreaEditorIfEditingParamIsSet();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error occurred in processing design area list change:', e);
        }
      }
    );

    Services.store.observeStore(
      (state) => state.designArea.maxZoom2d,
      (maxZoom2d) => {
        Services.visualization.update2DMaxZoom(maxZoom2d);
      }
    );
    // in calculation widget mode the canvas events should be disabled,
    // the update is done directly after the modifications
    if (isWidget(location.pathname)) {
      this.toggleCanvasDataUpdate(false);
      Services.store.observeStore(
        getSelectedDesignProductionMethodIdentifier,
        (selectedDesignProductionMethod) => {
          updateColorAmountOnDesignProductionMethodChange(selectedDesignProductionMethod);
        }
      );
    }

    Services.store.observeStore(
      ({ configurator: { selectedoptionclassification } }) => selectedoptionclassification,
      this.componentChangeHandler.bind(this)
    );

    setupDesigner(this);
  }
  calculateObjectPosition(canvas) {
    const canvasData = this._serializeCanvas(canvas);
    const textObject = canvasData ? canvasData.objects.find((obj) => obj.type === 'Text') : [];
    const imageObject = canvasData ? canvasData.objects.find((obj) => obj.type === 'Image') : [];
    const canvasTextObject = canvas.objects.find((obj) => obj.uuid === textObject?.uuid);
    const canvasImageObject = canvas.objects.find((obj) => obj.uuid === imageObject?.uuid);
    if (canvasTextObject && canvasImageObject) {
      const canvasImageObjectSize = canvasImageObject.getBoundSize();
      const canvasTextObjectSize = canvasTextObject.getBoundSize();
      textObject.y = imageObject.y + canvasImageObjectSize.height / 2 + canvasTextObjectSize.height;
      const objects = [].concat(textObject, imageObject);
      return { ...canvasData, objects };
    }
    return canvasData;
  }
}
